import type { ChangeEvent } from 'react';
import { useContext, useEffect, useState } from 'react';
import { Tooltip } from 'bootstrap';
import { SmartContext } from '../../../library/Core/SmartContext';
import { debounce, dedupeFreeTextFilterOptions } from '../../../library/Core/SmartFunctions';
import DomainExpertDirectoryService from './DomainExpertDirectoryService';
import { handleClearAllFilterClick } from './DomainExpertSearchControl';

const imgAllExperts     = `${process.env.REACT_APP_IMAGE_BASEPATH}/potential-recruiters.svg`;
const imgAnsweredQuery  = `${process.env.REACT_APP_IMAGE_BASEPATH}/mentor-chat.svg`;
const imgWebinar        = `${process.env.REACT_APP_IMAGE_BASEPATH}/mentor-video.svg`;
const imgGuestLecture   = `${process.env.REACT_APP_IMAGE_BASEPATH}/mentor-schedule.svg`;
const imgMaxPotential   = `${process.env.REACT_APP_IMAGE_BASEPATH}/shuttle.svg`;

const cardsConfig = [
    {
        id: 'totalDomainExperts',
        label: 'All Domain Expert',
        image: imgAllExperts,
        imageWidth: 65,
        tooltip: 'Total alumni identified as domain experts by the system.',
    },
    {
        id: 'answeredQueries',
        label: 'Answered Queries',
        image: imgAnsweredQuery,
        imageWidth: 54,
        tooltip: 'Domain experts who have replied to messages from students or other alumni.',
    },
    {
        id: 'contributedWebinars',
        label: 'Contributed webinars',
        image: imgWebinar,
        imageWidth: 47,
        tooltip: 'Domain experts who have conducted at least one webinar (webinar_counts > 0 for the institute).',
    },
    {
        id: 'conductedGuestLectures',
        label: 'Conducted Guest Lectures',
        image: imgGuestLecture,
        imageWidth: 49,
        tooltip: 'Domain experts with an enabled L&D Events marker containing at least one "Conducted Guest Lectures" entry.',
    },
    {
        id: 'maxPotentialBatch',
        label: 'Maximum Potential',
        image: imgMaxPotential,
        imageWidth: 49,
        tooltip: 'Batch and program with the highest number of domain experts.',
    },
];

const DomainExpertSummaryCardControl = () => {
    const { state, dispatch } = useContext(SmartContext);
    const summaryData = (state as any).data?.summary || {};
    const [searchName, setSearchName]   = useState('');
    const [showResetButton, setShowResetButton] = useState(false);
    const [isShowSearch, setShowSearch] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    useEffect(() => {
        const defaultFilters = DomainExpertDirectoryService.getDefaultSearchCriteria();
        const hasActiveFilter =
            JSON.stringify((state as any).data?.filters) !== JSON.stringify(defaultFilters);
        setShowResetButton(hasActiveFilter);
    }, [(state as any).data?.filters]);

    // Build autocomplete suggestions from existing students data
    const students = (state as any).data?.students || [];
    const searchLower = searchName.toLowerCase();

    // Unique matching names (keyed by lowercase full name)
    const nameMap = new Map<string, { text: string; student: any }>();
    const expertiseTags: string[] = [];

    students.forEach((s: any) => {
        const fullName = (s.name || `${s.firstName || ''} ${s.lastName || ''}`).trim();
        if (fullName.toLowerCase().includes(searchLower)) {
            const nameKey = fullName.toLowerCase();
            if (!nameMap.has(nameKey)) {
                nameMap.set(nameKey, { text: fullName, student: s });
            }
        }
        if (s.domainExpertiseArea) {
            s.domainExpertiseArea.split(',').forEach((area: string) => {
                const trimmed = area.trim();
                if (trimmed.toLowerCase().includes(searchLower)) {
                    expertiseTags.push(trimmed);
                }
            });
        }
    });

    const nameSuggestions = Array.from(nameMap.values()).map(({ text, student: s }) => ({
        id: `name-${s.uuid || s.id}`,
        type: 'name',
        text,
        subtext: s.domainExpertiseArea || '',
    }));

    const expertiseSuggestions = dedupeFreeTextFilterOptions(expertiseTags).map(({ value: text }, i) => ({
        id: `expertise-${i}-${text.toLowerCase()}`,
        type: 'expertise',
        text,
        subtext: 'Expertise',
    }));

    const seenSuggestionText = new Set<string>();
    const suggestions = [...nameSuggestions, ...expertiseSuggestions]
        .filter((s) => {
            const key = s.text.toLowerCase();
            if (seenSuggestionText.has(key)) return false;
            seenSuggestionText.add(key);
            return true;
        })
        .slice(0, 10);

    const handleSuggestionClick = (text: string) => {
        setSearchName(text);
        setShowSuggestions(false);
        const criteria: any = { ...(state as any).data?.filters, name: text };
        DomainExpertDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchName(val);
        setShowSuggestions(val.length > 0);
        debouncedSearch(val);
    };

    useEffect(() => {
        const tooltipEls = [].slice.call(document.querySelectorAll('.defaultTooltip'));
        const tips = tooltipEls.map((el: any) => new Tooltip(el));
        return () => tips.forEach((t: any) => t.dispose());
    });

    const getCardBorder = (cardId: string) =>
        cardId === (state as any).data?.selectedCard ? 'active' : '';

    const handleCardClick = (cardId: string) => {
        const count = summaryData[cardId];
        if (!count || count <= 0) {
            dispatch({ type: 'REFRESH_DATA', payload: { data: { ...(state as any).data, students: [] } } });
            return;
        }
        const criteria = {
            ...DomainExpertDirectoryService.getDefaultSearchCriteria(),
            ...DomainExpertDirectoryService.getCriteriaForSelectedCard(cardId, state),
            context: { level: 'MY_SCHOOL', page: 1, pageSize: 20, selectedCard: cardId },
        };
        DomainExpertDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);
    };

    const debouncedSearch = debounce((value: string) => {
        const criteria: any = { ...(state as any).data?.filters, name: value };
        DomainExpertDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);
    }, 200);

    const handleNameSearchClear = () => {
        setSearchName('');
        setShowSuggestions(false);
        debouncedSearch('');
    };

    const handleInputBlur = () => {
        // Small delay to allow click on suggestion to fire before hiding
        setTimeout(() => setShowSuggestions(false), 200);
    };

    return (
        <section className="bg-red pt-3 pt-sm-5 pb-3 container-fluid" id="alumni_directory" data-aos="fade-down">
            <div className="max-1140 mx-auto">
                <div className="row alumni-directory">
                    <div className="col-lg-12 alumni-directory-header">
                        <div className="alumni-directory-header_search">
                            <h2
                                data-aos="fade-left"
                                data-aos-delay="300"
                                className="font-22 font-600 text-white mb-3 mb-sm-0 me-3 aos-init aos-animate">
                                Domain Expert <span className="d-block d-sm-inline-block">Directory</span>
                            </h2>

                            <div className={`alumni-directorySearch-wrap ${isShowSearch ? 'show' : ''} position-relative`}>
                                <form
                                    className="alumni-directorySearch height-45"
                                    onSubmit={(e) => e.preventDefault()}>
                                    <button type="button" className="btn icon-search height-45"></button>
                                    <input
                                        className="form-control height-45"
                                        type="search"
                                        placeholder="Search by Name or Area of Expertise"
                                        aria-label="Search"
                                        value={searchName}
                                        onChange={handleInputChange}
                                        onBlur={handleInputBlur}
                                        onFocus={() => searchName.length > 0 && setShowSuggestions(true)}
                                    />
                                    <a className="btn-cross" onClick={handleNameSearchClear}></a>
                                </form>
                                {showSuggestions && (
                                    <div className="suggestions-dropdown position-absolute bg-white rounded shadow-sm w-100 mt-1" style={{ zIndex: 1050, top: '100%', left: 0, maxHeight: 250, overflowY: 'auto' }}>
                                        {suggestions.length > 0 ? (
                                            suggestions.map((s: any) => (
                                                <div
                                                    key={s.id}
                                                    className="suggestion-item px-3 py-2 cursor-pointer hover-bg-light"
                                                    style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                                    onMouseDown={() => handleSuggestionClick(s.text)}
                                                >
                                                    <div className="font-14 font-500 text-dark">{s.text}</div>
                                                    {s.subtext && (
                                                        <div className="font-12 text-muted text-truncate">{s.subtext}</div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-muted font-14">No matching results</div>
                                        )}
                                    </div>
                                )}
                                <a
                                    className="btn-alumniSearchToggle cursor-pointer icon-search d-flex d-sm-none"
                                    onClick={() => setShowSearch(!isShowSearch)}>
                                </a>
                            </div>

                            {showResetButton && (
                                <div className={`alumni-directorySearch-wrap ${isShowSearch ? 'show' : ''}`}>
                                    <button
                                        type="button"
                                        className="ms-2 btn btn-white-border height-40 mt-0"
                                        onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                        <span>Reset</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="aldir_scroll myCampus active">
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5">
                        {cardsConfig.map((card) => (
                            <div key={card.id} className="col mb-4 aldir_box">
                                <div
                                    className={`bg-white rounded alumni-directory-block h-100 aos-init aos-animate ${getCardBorder(card.id)}`}
                                    data-aos="fade-up"
                                    data-aos-delay="900"
                                    role="button"
                                    onClick={() => handleCardClick(card.id)}>

                                    <div className="number font-28 font-600 d-flex justify-content-between align-items-center">
                                        {/* Maximum Potential card special rendering */}
                                        {card.id === 'maxPotentialBatch' ? (
                                            <>
                                                {summaryData.maxPotentialBatch ?? 0}
                                                <span className="font-16 font-500 text-dark-blue ms-1">(Batch)</span>
                                                <i
                                                    className="icon-info font-16 text-sky-blue defaultTooltip ms-1"
                                                    data-bs-toggle="tooltip"
                                                    title={summaryData.maxPotentialProgram ?? 'N/A'}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {summaryData[card.id] ?? 0}
                                                <i
                                                    className="bi bi-info-circle-fill defaultTooltip font-16 position-absolute top-10 right-10"
                                                    data-bs-toggle="tooltip"
                                                    title={card.tooltip}
                                                />
                                            </>
                                        )}
                                    </div>

                                    <div className="d-flex justify-content-between">
                                        <div className="font-16 font-500 flex-1">{card.label}</div>
                                        <div className="alumni-directory-img me-3 mb-3">
                                            <img
                                                src={card.image}
                                                className="img-fluid"
                                                width={card.imageWidth}
                                                alt={card.label}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DomainExpertSummaryCardControl;